/**
 * App root — React Router setup with Layout wrapper and auth routing.
 *
 * Route structure:
 * - /login            Login/Register (no layout, full-page)
 * - /                 Dashboard (public — shows feature cards)
 * - /evaluate         New Evaluation (protected)
 * - /evaluations/:id  Evaluation results (protected)
 * - /mock             Mock Interview (protected)
 * - /generator        AI Answer Generator (protected)
 * - /questions        Question Bank (public — browsing is free)
 * - /moderation       Moderation Queue (protected — moderator-only API)
 * - /coaching         Coach Dashboard (protected — invite/manage students)
 * - /templates        Answer Templates (protected — manage reusable STAR frameworks)
 * - /analytics        Advanced Analytics (protected)
 * - /shared/:token    Shared evaluation (public, read-only)
 * - /pricing          Pricing/Plans (public)
 * - /billing/success  Post-checkout success (protected)
 *
 * Why some routes are public:
 *   The dashboard and question bank are discovery surfaces. Letting
 *   users see what's available before signing up reduces friction.
 *   The actual "do work" routes (evaluate, mock, generator) require auth
 *   so we can track history and usage.
 */

import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import PageEventObserver from "./analytics/PageEventObserver";
import AppRail from "./components/AppRail";
import ProtectedRoute from "./components/ProtectedRoute";
import CoachRoute from "./components/CoachRoute";
import ModeratorRoute from "./components/ModeratorRoute";
import InternalRoute from "./components/InternalRoute";
import AdminRoute from "./components/AdminRoute";
import Dashboard from "./pages/Dashboard";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import NewEvaluation from "./pages/NewEvaluation";
import EvaluationDetail from "./pages/EvaluationDetail";
import MockInterview from "./pages/MockInterview";
import AnswerGenerator from "./pages/AnswerGenerator";
import QuestionBank from "./pages/QuestionBank";
import VersionComparison from "./pages/VersionComparison";
import Analytics from "./pages/Analytics";
import PricingPage from "./pages/PricingPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import RefundPolicyPage from "./pages/RefundPolicyPage";
import ContactPage from "./pages/ContactPage";
import ModerationQueue from "./pages/ModerationQueue";
import CoachDashboard from "./pages/CoachDashboard";
import SharedEvaluation from "./pages/SharedEvaluation";
import Templates from "./pages/Templates";
import NotFound from "./pages/NotFound";
import CourseHome from "./pages/CourseHome";
import CourseLevelPage from "./pages/CourseLevelPage";
import ScenarioPage from "./pages/ScenarioPage";
import ResumeMasteryLanding from "./pages/ResumeMasteryLanding";
import ATSScorePage from "./pages/ATSScorePage";
import ResumeWorkspaceHome from "./pages/ResumeWorkspaceHome";
import ResumeTargetWorkspace from "./pages/ResumeTargetWorkspace";
import VisibilityStrategyPage from "./pages/VisibilityStrategyPage";
import LinkedInPresenceHome from "./pages/LinkedInPresenceHome";
import LinkedInProfilePage from "./pages/LinkedInProfilePage";
import LinkedInContentPage from "./pages/LinkedInContentPage";
import LinkedInOutreachPage from "./pages/LinkedInOutreachPage";
import AgenticMasteryHome from "./pages/AgenticMasteryHome";
import AgenticMasteryModulePage from "./pages/AgenticMasteryModulePage";
import AgenticOpsHome from "./pages/AgenticOpsHome";
import ClaudeHarnessHome from "./pages/ClaudeHarnessHome";
import HarnessScorecardPage from "./pages/HarnessScorecardPage";
import ClaudeHarnessModulePage from "./pages/ClaudeHarnessModulePage";
import AgenticOpsModulePage from "./pages/AgenticOpsModulePage";
import AgenticMasteryProjectPage from "./pages/AgenticMasteryProjectPage";
import PortfolioKitHome from "./pages/PortfolioKitHome";
import PortfolioArchetypePage from "./pages/PortfolioArchetypePage";
import READMETemplatePage from "./pages/READMETemplatePage";
import DeploymentChecklistPage from "./pages/DeploymentChecklistPage";
import LovablePortfolioPromptPage from "./pages/LovablePortfolioPromptPage";
import PortfolioDemoPage from "./pages/PortfolioDemoPage";
import PortfolioModulePage from "./pages/PortfolioModulePage";
import CareerTransitionHome from "./pages/CareerTransitionHome";
import EarlyCareerHome from "./pages/EarlyCareerHome";
import TeardownPage from "./pages/TeardownPage";
import ReadinessAssessmentPage from "./pages/ReadinessAssessmentPage";
import RoadmapPage from "./pages/RoadmapPage";
import RoadmapHistoryPage from "./pages/RoadmapHistoryPage";
import FrameworkPage from "./pages/FrameworkPage";
import DesignLabPage from "./pages/DesignLabPage";
import RoadmapPrimitivesLabPage from "./pages/RoadmapPrimitivesLabPage";
import AIInterviewCoachHome from "./pages/AIInterviewCoachHome";
import ForgeCoachPage from "./pages/ForgeCoachPage";
import ForgeCoachHome from "./pages/ForgeCoachHome";
import DebugExerciseWidgetPage from "./pages/DebugExerciseWidgetPage";
import AdminPromptsPage from "./pages/AdminPromptsPage";
import AdminMetricsPage from "./pages/AdminMetricsPage";

function App() {
  return (
    <BrowserRouter>
      <PageEventObserver />
      <Routes>
        {/* Login page — full-screen, no sidebar layout */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Standalone debug-exercise widget — chrome-free for iframe embeds
            from Agentic Mastery slide decks (no AppRail sidebar). */}
        <Route
          path="/learn/agentic-mastery/:slug/exercises/:exerciseId"
          element={<DebugExerciseWidgetPage />}
        />

        {/* Unified AppRail shell (replaces Layout/AppLayout/PublicLayout) */}
        <Route element={<AppRail />}>
          {/* Public routes */}
          <Route path="/" element={<Dashboard />} />
          <Route path="/questions" element={<QuestionBank />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/refunds" element={<RefundPolicyPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/coach" element={<AIInterviewCoachHome />} />
          <Route path="/shared/:token" element={<SharedEvaluation />} />
          {/* Course routes — Level 1 public, Levels 2–4 access-gated by the page itself */}
          <Route path="/agentic-interviewing" element={<CourseHome />} />
          <Route path="/agentic-interviewing/level/:levelNumber" element={<CourseLevelPage />} />
          {/* Scenario pages — standalone routes for Phase 4 system design scenarios */}
          <Route path="/agentic-interviewing/scenario/:slug" element={<ScenarioPage />} />

          {/* Visibility Strategy Workshop — free lead magnet, routes into full product line */}
          <Route path="/visibility-strategy" element={<VisibilityStrategyPage />} />

          {/* LinkedIn Presence Builder (Product #6) */}
          <Route path="/linkedin-presence" element={<LinkedInPresenceHome />} />
          <Route path="/linkedin-presence/profile" element={<LinkedInProfilePage />} />
          <Route path="/linkedin-presence/content" element={<LinkedInContentPage />} />
          <Route path="/linkedin-presence/outreach" element={<LinkedInOutreachPage />} />

          {/* Forge Coach (Pro agent).
              /forge-coach/about — public positioning surface (D-44).
              /forge-coach — work surface: the page itself renders an explicit
              sign-in gate for logged-out visitors (D-28, no silent redirect),
              a tier-upgrade CTA for free users, and the workspace for Pro/AA. */}
          <Route path="/forge-coach/about" element={<ForgeCoachHome />} />
          <Route path="/forge-coach" element={<ForgeCoachPage />} />

          {/* Agentic AI Mastery (Product #1) */}
          <Route path="/agentic-ai-mastery" element={<AgenticMasteryHome />} />
          <Route path="/agentic-ai-mastery/module/:slug" element={<AgenticMasteryModulePage />} />
          <Route path="/agentic-ai-mastery/project/:id" element={<AgenticMasteryProjectPage />} />
          {/* Agentic Ops (operations mini course) — companion to Agentic AI Mastery */}
          <Route path="/agentic-ops" element={<AgenticOpsHome />} />
          <Route path="/agentic-ops/module/:slug" element={<AgenticOpsModulePage />} />
          <Route path="/claude-harness" element={<ClaudeHarnessHome />} />
          <Route path="/claude-harness/scorecard" element={<HarnessScorecardPage />} />
          <Route path="/claude-harness/module/:slug" element={<ClaudeHarnessModulePage />} />
          {/* Career Transition (Product #2) — the quarterback orchestrator */}
          <Route path="/career-transition" element={<CareerTransitionHome />} />
          <Route
            path="/career-transition/assessment"
            element={
              <ProtectedRoute>
                <ReadinessAssessmentPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/career-transition/roadmap"
            element={
              <ProtectedRoute>
                <RoadmapPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/career-transition/roadmap/:id"
            element={
              <ProtectedRoute>
                <RoadmapPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/career-transition/roadmap-history"
            element={
              <ProtectedRoute>
                <RoadmapHistoryPage />
              </ProtectedRoute>
            }
          />
          {/* Framework deep-dives — readiness-assessment is public (free tier),
              the other 5 have a paywall gate in FrameworkPage itself */}
          <Route path="/career-transition/framework/:slug" element={<FrameworkPage />} />

          {/* Design Lab — internal surface for pre-Designer-session comparison.
              Gated by REACT_APP_ENABLE_INTERNAL_TOOLS=true at build time AND
              requires authentication. Returns a 404-style page otherwise so
              the URL does not advertise an internal route. */}
          <Route
            path="/design-lab"
            element={
              <InternalRoute>
                <DesignLabPage />
              </InternalRoute>
            }
          />
          <Route
            path="/design-lab/primitives"
            element={
              <InternalRoute>
                <RoadmapPrimitivesLabPage />
              </InternalRoute>
            }
          />

          {/* Proof-Forge founding-cohort landing (Path B) — public marketing
              page for cold/logged-out traffic; intentionally NOT ProtectedRoute,
              same as /portfolio-kit. */}
          <Route path="/early-career" element={<EarlyCareerHome />} />
          {/* The free teardown — unlisted lead magnet; link delivered on email submit */}
          <Route path="/early-career/teardown" element={<TeardownPage />} />

          {/* Portfolio Kit (Product #3) — Track B projects playbook + Track A Lovable site builder */}
          <Route path="/portfolio-kit" element={<PortfolioKitHome />} />
          <Route path="/portfolio-kit/archetype/:slug" element={<PortfolioArchetypePage />} />
          <Route path="/portfolio-kit/readme-template" element={<READMETemplatePage />} />
          <Route path="/portfolio-kit/deployment-checklist" element={<DeploymentChecklistPage />} />
          <Route path="/portfolio-kit/lovable-prompt" element={<LovablePortfolioPromptPage />} />
          <Route path="/portfolio-kit/demo" element={<PortfolioDemoPage />} />
          <Route path="/portfolio-kit/modules/:slug" element={<PortfolioModulePage />} />

          {/* Resume Mastery — The AI/ML Job Search Edition (Product #4) */}
          <Route path="/resume-mastery" element={<ResumeMasteryLanding />} />

          {/* ATS scorer — now public (no ProtectedRoute) */}
          <Route path="/resume-mastery/ats-score" element={<ATSScorePage />} />

          {/* Retired prompt-kit pages → redirect to the landing (SEO: nginx 301
              handles the real redirect in prod; this covers SPA/dev nav). */}
          <Route path="/resume-mastery/principles" element={<Navigate to="/resume-mastery" replace />} />
          <Route path="/resume-mastery/prompts" element={<Navigate to="/resume-mastery" replace />} />
          <Route path="/resume-mastery/prompts/:slug" element={<Navigate to="/resume-mastery" replace />} />
          <Route path="/resume-mastery/demos" element={<Navigate to="/resume-mastery" replace />} />
          <Route path="/resume-mastery/demos/:slug" element={<Navigate to="/resume-mastery" replace />} />
          <Route path="/resume-mastery/progress" element={<Navigate to="/resume-mastery" replace />} />

          <Route
            path="/resume-mastery/workspace"
            element={<ProtectedRoute><ResumeWorkspaceHome /></ProtectedRoute>}
          />
          <Route
            path="/resume-mastery/workspace/targets/:id"
            element={<ProtectedRoute><ResumeTargetWorkspace /></ProtectedRoute>}
          />
          <Route
            path="/billing/success"
            element={
              <ProtectedRoute>
                <PricingPage />
              </ProtectedRoute>
            }
          />

          {/* Protected routes — require authentication */}
          <Route
            path="/evaluate"
            element={
              <ProtectedRoute>
                <NewEvaluation />
              </ProtectedRoute>
            }
          />
          <Route
            path="/evaluations/:id"
            element={
              <ProtectedRoute>
                <EvaluationDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/mock"
            element={
              <ProtectedRoute>
                <MockInterview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/generator"
            element={
              <ProtectedRoute>
                <AnswerGenerator />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <ProtectedRoute>
                <Analytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/answers/:answerId/compare"
            element={
              <ProtectedRoute>
                <VersionComparison />
              </ProtectedRoute>
            }
          />
          <Route
            path="/moderation"
            element={
              <ModeratorRoute>
                <ModerationQueue />
              </ModeratorRoute>
            }
          />
          <Route
            path="/coaching"
            element={
              <CoachRoute>
                <CoachDashboard />
              </CoachRoute>
            }
          />
          <Route
            path="/templates"
            element={
              <ProtectedRoute>
                <Templates />
              </ProtectedRoute>
            }
          />

          {/* Prompt Admin (admin-only) — edit/version/deploy product prompts */}
          <Route
            path="/admin/prompts"
            element={
              <AdminRoute>
                <AdminPromptsPage />
              </AdminRoute>
            }
          />

          {/* Founder Metrics (admin-only) — cross-user North-star + funnel */}
          <Route
            path="/admin/metrics"
            element={
              <AdminRoute>
                <AdminMetricsPage />
              </AdminRoute>
            }
          />
        </Route>

        {/* 404 catch-all */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
